/**
 * Firebase Storage Service
 *
 * Provides file upload, download, and management functionality.
 */

import {
  ref,
  uploadBytesResumable,
  getDownloadURL as firebaseGetDownloadURL,
  deleteObject,
  UploadTaskSnapshot,
} from 'firebase/storage';
import { storage } from './firebase';

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  percentage: number;
  state: 'running' | 'paused' | 'success' | 'canceled' | 'error';
}

export interface UploadResult {
  url: string;
  path: string;
}

export interface ImageUploadOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}

/**
 * Upload a file to Firebase Storage
 */
export async function uploadFile(
  path: string,
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot: UploadTaskSnapshot) => {
        const progress: UploadProgress = {
          bytesTransferred: snapshot.bytesTransferred,
          totalBytes: snapshot.totalBytes,
          percentage: (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
          state: snapshot.state,
        };

        if (onProgress) {
          onProgress(progress);
        }
      },
      (error) => {
        reject(new Error(`Upload failed: ${error.message}`));
      },
      async () => {
        try {
          const url = await firebaseGetDownloadURL(uploadTask.snapshot.ref);
          resolve({ url, path });
        } catch (error) {
          reject(error);
        }
      }
    );
  });
}

/**
 * Upload an image with optional resizing
 */
export async function uploadImage(
  path: string,
  file: File,
  options: ImageUploadOptions = {},
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  const { maxWidth = 1920, maxHeight = 1080, quality = 0.85 } = options;

  // Check if file is an image
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  // Resize image if needed
  const resizedFile = await resizeImage(file, maxWidth, maxHeight, quality);

  return uploadFile(path, resizedFile, onProgress);
}

/**
 * Upload multiple files
 */
export async function uploadFiles(
  files: Array<{ path: string; file: File }>,
  onProgress?: (index: number, progress: UploadProgress) => void
): Promise<UploadResult[]> {
  const results: UploadResult[] = [];

  for (let i = 0; i < files.length; i++) {
    const { path, file } = files[i];
    const result = await uploadFile(path, file, (progress) => {
      if (onProgress) {
        onProgress(i, progress);
      }
    });
    results.push(result);
  }

  return results;
}

/**
 * Get download URL for a file
 */
export async function getDownloadURL(path: string): Promise<string> {
  const storageRef = ref(storage, path);
  return firebaseGetDownloadURL(storageRef);
}

/**
 * Delete a file from storage
 */
export async function deleteFile(path: string): Promise<void> {
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
}

/**
 * Delete multiple files from storage
 */
export async function deleteFiles(paths: string[]): Promise<void> {
  await Promise.all(paths.map((path) => deleteFile(path)));
}

/**
 * Generate a unique file path for listing photos
 */
export function getListingPhotoPath(listingId: string, fileName: string): string {
  const timestamp = Date.now();
  const extension = fileName.split('.').pop() || 'jpg';
  return `listings/${listingId}/photos/${timestamp}.${extension}`;
}

/**
 * Generate a unique file path for user profile photos
 */
export function getUserPhotoPath(userId: string, fileName: string): string {
  const extension = fileName.split('.').pop() || 'jpg';
  return `users/${userId}/profile.${extension}`;
}

/**
 * Generate a unique file path for message attachments
 */
export function getMessageAttachmentPath(threadId: string, fileName: string): string {
  const timestamp = Date.now();
  const extension = fileName.split('.').pop() || 'file';
  return `messages/${threadId}/attachments/${timestamp}.${extension}`;
}

// ============ HELPER FUNCTIONS ============

/**
 * Resize an image file
 */
async function resizeImage(
  file: File,
  maxWidth: number,
  maxHeight: number,
  quality: number
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      let { width, height } = img;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Could not create blob'));
            return;
          }

          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });

          resolve(resizedFile);
        },
        file.type,
        quality
      );
    };

    img.onerror = () => {
      reject(new Error('Could not load image'));
    };

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Get file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Validate file type
 */
export function isValidImageType(file: File): boolean {
  const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  return validTypes.includes(file.type);
}

/**
 * Validate file size (default max 10MB)
 */
export function isValidFileSize(file: File, maxSizeMB = 10): boolean {
  return file.size <= maxSizeMB * 1024 * 1024;
}
